// src/App.jsx (Finalized structure for Menu)
import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, Routes, Route } from "react-router-dom";
import Shop from "./pages/Shop";
import Product from "./pages/Product";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import Home from "./pages/Home";
import Ueber from "./pages/Ueber";
import Kontakt from "./pages/Kontakt";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import Impressum from "./pages/Impressum";
import Aufbau from "./pages/Aufbau";
import Datenschutz from "./pages/Datenschutz";
import Versand from "./pages/Versand";
import Successes from "./pages/Successes";
import Footer from "./components/layout/Footer";
import CookieConsent from "./components/CookieConsent";
import WhatsAppButton from "./components/WhatsAppButton";
import AOS from "aos";
import { X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

function CartIcon({ count = 0 }) {
    return (
        <span className="relative inline-flex">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M3 3h2l.4 2M7 13h9l3-8H6.4" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="9" cy="19" r="1.5" /><circle cx="17" cy="19" r="1.5" />
            </svg>
            {count > 0 && <span className="absolute -top-1 -right-1 text-[10px] leading-none px-1.5 py-0.5 rounded-full bg-brand-600 text-white font-bold">{count}</span>}
        </span>
    );
}

export default function App() {
    const [cart, setCart] = useState(() => {
        try {
            const raw = JSON.parse(localStorage.getItem("cart") || "[]");
            return Array.isArray(raw) ? raw : [];
        } catch { return []; }
    });
    useEffect(() => localStorage.setItem("cart", JSON.stringify(cart)), [cart]);

    const addToCart = (slug, qty = 1, meta = {}) => {
        setCart(prev => {
            const next = [...prev];
            const variant_id = meta?.variant_id ?? null;
            const key = `${slug}:${variant_id ?? "base"}`;
            const idx = next.findIndex(x => `${x.slug}:${x.variant_id ?? "base"}` === key);
            if (idx >= 0) next[idx] = { ...next[idx], qty: next[idx].qty + qty };
            else next.push({ slug, qty, title: meta?.title, variant_id, variant_label: meta?.variant_label, price_cents: meta?.price_cents, weight_grams: meta?.weight_grams });
            return next;
        });
    };
    const updateQty = (slug, qty, variant_id = null) => {
        const key = `${slug}:${variant_id ?? "base"}`;
        setCart(prev => prev.map(x => `${x.slug}:${x.variant_id ?? "base"}` === key ? { ...x, qty: Math.max(1, qty) } : x));
    };
    const removeFromCart = (slug, variant_id = null) => {
        const key = `${slug}:${variant_id ?? "base"}`;
        setCart(prev => prev.filter(x => `${x.slug}:${x.variant_id ?? "base"}` !== key));
    };
    const clearCart = () => setCart([]);

    // Warenkorb aus einer Erinnerungs-Mail zurückholen.
    // Bereits vorhandene Positionen bleiben unverändert – so verdoppelt
    // sich nichts, wenn der Kunde zwischenzeitlich selbst etwas gewählt hat.
    const restoreCart = (items = []) => {
        setCart(prev => {
            const next = [...prev];
            for (const it of items) {
                if (!it?.slug) continue;
                const variant_id = it.variant_id ?? null;
                const key = `${it.slug}:${variant_id ?? "base"}`;
                if (next.some(x => `${x.slug}:${x.variant_id ?? "base"}` === key)) continue;
                next.push({
                    slug: it.slug,
                    qty: Math.max(1, Number(it.qty) || 1),
                    title: it.title,
                    variant_id,
                    variant_label: it.variant_label ?? null,
                    price_cents: it.price_cents,
                });
            }
            return next;
        });
    };
    const cartCount = cart.reduce((n, it) => n + it.qty, 0);
    
    const [menuOpen, setMenuOpen] = useState(false);
    const location = useLocation();
    const headerRef = useRef(null);

    useEffect(() => {
        const stored = localStorage.getItem("theme") || "light";
        document.documentElement.classList.toggle("dark", stored === "dark");
    }, []);

    useEffect(() => {
        AOS.refresh();
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, [location]);

    useEffect(() => {
        document.body.style.overflow = menuOpen ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [menuOpen]);

    return (
        <div className="min-h-screen bg-white text-slate-900 dark:bg-slate-950 dark:text-slate-100 font-sans selection:bg-brand-100 selection:text-brand-900">
            <header ref={headerRef} className="sticky top-0 z-50 border-b border-slate-200/50 bg-white/70 backdrop-blur-xl dark:bg-slate-900/70 dark:border-slate-800/50">
                <div className="mx-auto max-w-6xl px-4 py-4 flex items-center justify-between">
                    <NavLink to="/" className="flex items-center gap-4 active:scale-95 transition-transform" onClick={() => setMenuOpen(false)}>
                        <img src="/images/logo.webp" alt="THB Logo" className="h-10 w-auto object-contain" />
                        <div className="flex flex-col leading-none">
                            <span className="text-lg md:text-xl font-heading font-black text-slate-900 dark:text-white uppercase tracking-tight">Tuscher <span className="text-brand-600">Helmut</span></span>
                            <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em] mt-0.5">Betonrohrfallen</span>
                        </div>
                    </NavLink>

                    <nav className="hidden md:block">
                        <ul className="flex items-center gap-8">
                            {[{ to: "/", label: "Start" }, { to: "/shop", label: "Shop" }, { to: "/ueber", label: "Über" }, { to: "/aufbau", label: "Aufbau" }, /* { to: "/erfolge", label: "Erfolge" }, */ { to: "/blog", label: "Blog" }, { to: "/kontakt", label: "Kontakt" }].map((item) => (
                                <li key={item.to}>
                                    <NavLink to={item.to} end={item.to === "/"} className={({ isActive }) => `text-sm font-bold uppercase tracking-wider transition-all hover:text-brand-600 ${isActive ? 'text-brand-600' : 'text-slate-600 dark:text-slate-400'}`}>{item.label}</NavLink>
                                </li>
                            ))}
                            <li className="ml-4 pl-8 border-l border-slate-200 dark:border-slate-800">
                                <NavLink to="/warenkorb" className={({ isActive }) => `relative flex h-10 w-10 items-center justify-center rounded-xl bg-slate-50 dark:bg-slate-800/50 transition-all hover:scale-110 ${isActive ? 'text-brand-600 ring-2 ring-brand-500/20' : 'text-slate-600 dark:text-slate-300'}`}><CartIcon count={cartCount} /></NavLink>
                            </li>
                        </ul>
                    </nav>

                    <div className="flex items-center gap-3 md:hidden">
                        <NavLink to="/warenkorb" className="relative flex h-10 w-10 items-center justify-center rounded-xl bg-slate-50 dark:bg-slate-800/50 text-slate-600 dark:text-slate-300" onClick={() => setMenuOpen(false)}><CartIcon count={cartCount} /></NavLink>
                        <button onClick={() => setMenuOpen(!menuOpen)} className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-900 dark:bg-brand-600 text-white shadow-lg"><svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 6h16M4 12h16M4 18h16" /></svg></button>
                    </div>
                </div>

                <AnimatePresence>
                    {menuOpen && (
                        <div className="fixed inset-0 z-[100] md:hidden">
                            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setMenuOpen(false)} className="absolute inset-0 bg-slate-900/60 backdrop-blur-md" />
                            <motion.nav initial={{ y: "-100%" }} animate={{ y: 0 }} exit={{ y: "-100%" }} transition={{ type: "spring", damping: 25, stiffness: 200 }} className="relative flex flex-col bg-white dark:bg-slate-950 shadow-2xl overflow-hidden pt-20">
                                <div className="p-8 space-y-2">
                                    {[{ to: "/", label: "Start" }, { to: "/shop", label: "Shop" }, { to: "/ueber", label: "Über" }, { to: "/aufbau", label: "Aufbau" }, /* { to: "/erfolge", label: "Erfolge" }, */ { to: "/blog", label: "Blog" }, { to: "/kontakt", label: "Kontakt" }, { to: "/warenkorb", label: "Warenkorb" }].map((item, i) => (
                                        <motion.div key={item.to} initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.1 + i * 0.05 }}>
                                            <NavLink to={item.to} onClick={() => setMenuOpen(false)} className={({ isActive }) => `block py-3 text-3xl font-heading font-black uppercase tracking-tighter ${isActive ? "text-brand-600" : "text-slate-900 dark:text-white"}`}>{item.label}</NavLink>
                                        </motion.div>
                                    ))}
                                </div>
                                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="p-8 bg-slate-50 dark:bg-slate-900/50 flex items-center justify-between mt-4">
                                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">Helmut Tuscher <br/> Betonrohrfallen</div>
                                    <button onClick={() => setMenuOpen(false)} className="h-12 w-12 rounded-2xl bg-brand-600 text-white flex items-center justify-center shadow-lg"><X className="h-6 w-6" /></button>
                                </motion.div>
                            </motion.nav>
                        </div>
                    )}
                </AnimatePresence>
            </header>

            <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/ueber" element={<Ueber />} />
                <Route path="/shop" element={<Shop />} />
                <Route path="/produkt/:slug" element={<Product addToCart={addToCart} />} />
                <Route path="/warenkorb" element={<Cart cart={cart} updateQty={updateQty} removeFromCart={removeFromCart} restoreCart={restoreCart} />} />
                <Route path="/checkout" element={<Checkout cart={cart} clearCart={clearCart} />} />
                <Route path="/kontakt" element={<Kontakt />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/erfolge" element={<Successes />} />
                <Route path="/impressum" element={<Impressum />} />
                <Route path="/datenschutz" element={<Datenschutz />} />
                <Route path="/versand" element={<Versand />} />
                <Route path="/aufbau" element={<Aufbau />} />
                <Route path="*" element={<Home />} />
            </Routes>
            <CookieConsent />
            <WhatsAppButton />
            <Footer />
        </div>
    );
}